import React, { useState, useEffect, useRef } from "react";
import styles from "./Slider.module.scss";
import classNames from "classnames";

const calculateValue = (e, ref, step, max, min) => {
    const { clientX } = e;
    const { left, width } = ref.current.getBoundingClientRect();
    if ((clientX - left) > width) {
        return max;
    } else if (clientX < left) {
        return min;
    } else {
        const proportion = (clientX - left) / width;
        const calc = Math.round(((proportion * (max - min)) + min) / step) * step;
        return calc < min ? min : calc;
    }
};

const regexp = /^[0-9]*$/;

const Slider = ({
    form,
    name,
    onChange: _onChange,
    value: _value,
    className,
    style,
    disabled,
    min = 0,
    max = 100,
    step = 1,
    unitSuffix = "",
    ...rest
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const sliderRef = useRef(null);
    let onChange = _onChange;
    let value = _value;
    if (form && name) {
        value = _value || form.formState[name];
        onChange = onChange || form.handleFormChange;
    }
    const [inputValue, setInputValue] = useState(_value);
    const digits = max.toString().length;
    const percent = (value - min) / (max - min) * 100;

    const handleInput = (e) => {
        if (!regexp.test(e.target.value) && e.target.value != "") {
            return;
        }
        setInputValue(e.target.value);
        if (e.target.value > max) {
            onChange(max, { name });
        } else if (e.target.value < min || e.target.value === "") {
            onChange(min, { name });
        } else {
            onChange(Number(e.target.value), { name });
        }
    };

    const handleBlur = (e) => {
        if (e.target.value > max) {
            setInputValue(max);
        } else if (Number(e.target.value) <= min) {
            const fixMin = min === 0 ? '0' : min;
            setInputValue(fixMin);
        }
    };

    const handleMouseDown = (e) => {
        if (disabled) {
            return;
        }

        const value = calculateValue(e, sliderRef, step, max, min);
        setInputValue(value);
        onChange(value, { name });
        setIsDragging(true);
        e.stopPropagation();
        e.preventDefault();
    };

    useEffect(() => {
        if (isDragging) {
            const moveListener = (e) => {
                const value = calculateValue(e, sliderRef, step, max, min);
                setInputValue(value);
                onChange(value, { name });
            };

            const upListener = () => {
                setIsDragging(false);
            };

            window.addEventListener("mousemove", moveListener);
            window.addEventListener("mouseup", upListener);

            return () => {
                window.removeEventListener("mousemove", moveListener);
                window.removeEventListener("mouseup", upListener);
            }
        }
    }, [isDragging, setIsDragging, onChange, step, max, min, setInputValue, inputValue])

    return <div className={classNames(styles.base, className, { [styles['disabled']]: disabled })} style={style}>
        <div className={styles.indicator}><span><input type="number" disabled={disabled} onChange={handleInput}
            onBlur={handleBlur} name={name}
            value={inputValue} min={min} max={max} {...rest}
            style={{ width: 32 + digits * 8 }} />{unitSuffix}</span></div>
        <div className={styles.minMax}>{min}{unitSuffix}</div>
        <div className={styles.slider} ref={sliderRef}>
            <div className={styles.track} onMouseDown={handleMouseDown} />
            <div className={styles.progress} style={{ width: `${percent}%` }} onMouseDown={handleMouseDown} />
            <div className={styles.thumb} style={{ left: `${percent}%` }} onMouseDown={handleMouseDown} />
        </div>
        <div className={styles.minMax}>{max}{unitSuffix}</div>
    </div>
};

export default Slider;
